import Phaser from 'phaser';
import MainMenu from '../menu/MainMenu';
import ABCyaPhaser from '@abcya/abcya_phaser';
import gameData from '../gameData';
import MainButtonBar from '../menu/MainButtonBar';
import SoundAssetKeys from '../SoundAssetKeys';
import DropContainer from '../ui/DropContainer';
import ItemAction from '../menu/ItemAction';
import ConfirmDialog from '../menu/ConfirmDialog';
import ABCyaLib from '@abcya/abcya_lib';
import Piece from '../ui/Piece';
import Games from '../Games';

export default class Game extends Phaser.Scene {
  static get KEY() { return 'Game'; }

  create() {
    this.bg = ABCyaPhaser.Images.addImage(0,0, 'MainBackground_4x3', this).setOrigin(0);

    this.placedItems = [];
    this.animatedItems = [];
    this.itemTrash = [];
    this.actions = [];

    this.isFlipable = gameData.settings.FLIPS !== false;

    this.buttonContainer = new MainButtonBar(this);
    this.buttonContainer.on('UNDO', this.handleUndo, this);
    this.buttonContainer.on('START_OVER', this.handleStartOver, this);
    this.buttonContainer.on('SAVE_PICTURE', this.handleSavePicture, this);

    this.dropContainer = new DropContainer(this);
    this.dropContainer.on('ROTATE_ACTION', this.recordRotateAction, this);
    this.add.existing(this.dropContainer);
    this.add.existing(this.buttonContainer);

    this.menu = new MainMenu(this);
    this.menu.on('SELECTED_ITEM', this.createDragItem, this);
    this.menu.on('RELEASE_ITEM', this.releaseDragItem, this);
    this.menu.on('CHANGE_BASE_ITEM', this.changeBaseItem, this);
    this.menu.on('MENU_CHANGED', this.handleMenuChange, this);
    this.add.existing(this.menu);

    this.layoutRatio();
  }

  handleUndo () {
    if (this.actions.length <= 0) return;
    this.dropContainer.deselectItem();
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._undo, this);
    const lastAction = this.actions.pop();
    let point;
    switch (lastAction.action) {
      case 'ADD':
        const index = this.placedItems.indexOf(lastAction.itemRef);
        this.placedItems.splice(index, 1);
        this.itemTrash.push(lastAction.itemRef);
        lastAction.itemRef.goAway();
        break;

      case 'MOVE':
        point = this.dropContainer.getLocalPoint(lastAction.x, lastAction.y);
        lastAction.itemRef.x = point.x;
        lastAction.itemRef.y = point.y;
        break;

      case 'ROTATE':
        lastAction.itemRef.rotation = lastAction.rotation;
        this.dropContainer.setSelectedRotation(lastAction.rotation);
        break;

      case 'DELETE':
        const item = lastAction.itemRef;
        item.on('PICKUP_ITEM', this.pickupItem, this);
        item.on('RELEASE_ITEM', this.releaseDragItem, this);
        point = this.dropContainer.getLocalPoint(lastAction.x, lastAction.y);
        item.x = point.x;
        item.y = point.y;
        item.comeBack();
        this.placedItems.push(item);
        this.dropContainer.add(item);
        if (item.isAnimated) {
          this.animatedItems.push(item);
        }
        break;
    }
  }

  pickupItem (piece) {
    if (this.draggingItem) return;
    this.dropContainer.deselectItem();
    const index = this.placedItems.indexOf(piece);
    this.draggingItem = this.placedItems[index];

    const point = this.dropContainer.getWorldTransformMatrix().transformPoint(this.draggingItem.x, this.draggingItem.y);
    if (this.draggingItem.parentContainer !== null && this.draggingItem.parentContainer !== undefined) {
      this.draggingItem.parentContainer.remove(this.draggingItem);
    }
    this.add.existing(this.draggingItem);
    this.children.bringToTop(this.draggingItem);
    this.draggingItem.x = point.x;
    this.draggingItem.y = point.y;
    this.dragOffsetX = point.x - this.input.activePointer.x;
    this.dragOffsetY = point.y - this.input.activePointer.y;
    this.placedItems.splice(index, 1);

    this.tempAction = new ItemAction({ action: 'MOVE', itemRef: this.draggingItem, x: point.x, y: point.y });
    this.isDragging = true;
    this.lastDragX = this.input.activePointer.x; // we were flipping for some reason when moving into this new all
    if (gameData.settings.GAME_NAME === 'Fridge Magnets') {
      this.cheapFridgePlay(this.draggingItem.menuIndex, this.draggingItem.submenuIndex);
    }
  }

  cheapFridgePlay (menuNum, submenuNum) {
    let soundToPlay = '';
    if (menuNum === 1 || menuNum === 2) {
      const l = getLetterFromIndex(submenuNum - 1);

      soundToPlay = `LetterSound_${l}`;
    }
    else if (menuNum === 3) {
      let num = submenuNum;
      if (submenuNum > 10) {
        num = submenuNum - 10;
      }
      soundToPlay = `Sound${num - 1}`;
    }
    else if (menuNum === 4) {
      let num = submenuNum;
      if (submenuNum > 9) {
        num = submenuNum - 9;
      }
      const symbolsounds = ['Circle', 'Square', 'Triangle', 'Star', 'Diamond','Heart', 'Crescent', 'Arrow', 'Rectangle'];
      soundToPlay = symbolsounds[num - 1];
    }
    else if (menuNum === 5) {
      const symbolsounds = ['ExclamationPoint', 'QuestionMark', 'Comma', 'Percent', 'DollarSign','Period', 'Plus', 'Minus', 'Multiply', 'Divide', 'Equals'];
      if (ABCyaLib.Environment.instance.locale === ABCyaLib.Environment.LOCALES.esUS) {
        symbolsounds.splice(0, 0, 'ExclamationPoint_1');
        symbolsounds.splice(2, 0, 'QuestionMark_1');
      }
      soundToPlay = symbolsounds[submenuNum - 1];
    }
    if (this.letterSound) this.letterSound.destroy();
    this.letterSound = null;
    this.letterSound = ABCyaPhaser.Audio.createSound(this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);
    ABCyaPhaser.Audio.playSound(soundToPlay, this.letterSound);
  }

  releaseDragItem () {
    if (!this.draggingItem) return;
    this.isDragging = false;
    const point = this.dropContainer.getLocalPoint(this.draggingItem.x, this.draggingItem.y);

    // Check for collision
    const alpha = this.textures.getPixelAlpha(point.x, point.y, this.dropContainer.dropBg.texture.key, this.dropContainer.dropBg.frame.name);

    if (alpha && alpha > 1) {
      this.draggingItem.on('PICKUP_ITEM', this.pickupItem, this);
      this.draggingItem.on('RELEASE_ITEM', this.releaseDragItem, this);
      this.placedItems.push(this.draggingItem);
      if (this.draggingItem.isAnimated) {
        this.animatedItems.push(this.draggingItem);
      }

      this.draggingItem.x = point.x;
      this.draggingItem.y = point.y;

      // Store action for undo
      this.actions.push(this.tempAction);
      this.tempAction = null;

      // If it's the first drop play a sound
      if (this.draggingItem.isPlaced === false) {
        const soundName = this.dropSoundAlt ? SoundAssetKeys._place_item_1 : SoundAssetKeys._place_item_2;
        this.dropSoundAlt = !this.dropSoundAlt;
        ABCyaPhaser.Audio.playOneShot(soundName, this);
      }
      this.draggingItem.isPlaced = true;

      this.dropContainer.add(this.draggingItem);
      this.dropContainer.selectItem(this.draggingItem);
    }
    else {
      if (this.draggingItem.isPlaced) {
        this.tempAction.action = 'DELETE';
        this.tempAction.itemSpriteName = this.draggingItem.spriteName;
        this.actions.push(this.tempAction);
      }
      this.itemTrash.push(this.draggingItem);
      const index = this.animatedItems.indexOf(this.draggingItem);
      this.animatedItems.splice(index,1);
      this.draggingItem.goAway();
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._undo, this);
    }
    this.tempAction = null;
    this.draggingItem = null;
  }

  handleStartOver () {
    this.dropContainer.deselectItem();
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._save_print_startOver, this);
    this.confirmDialog = new ConfirmDialog(this, 'ConfirmBackground', ABCyaPhaser.Localization.instance.getLocalizedString('start_over'));
    this.confirmDialog.on('YES', this.handleStartOverYes, this);
    this.confirmDialog.on('NO', this.hideConfirmDialog, this);
    this.add.existing(this.confirmDialog);
  }

  handleStartOverYes () {
    const count = this.placedItems.length;
    let item;
    for (let i = 0; i < count; i++) {
      item = this.placedItems[i];
      this.itemTrash.push(item);
      item.goAway();
    }
    this.placedItems = [];
    this.animatedItems = [];
    this.actions = [];
    this.hideConfirmDialog();
  }

  hideConfirmDialog () {
    this.confirmDialog.destroy();
    this.confirmDialog = null;
  }

  handleSavePicture () {
    const blocker = this.add.zone(0,0,this.scale.width, this.scale.height).setInteractive()
      .setOrigin(0);
    this.dropContainer.deselectItem();
    const rt = new Phaser.GameObjects.RenderTexture(this, 0,0,Math.ceil(this.dropContainer.dropBg.width),Math.ceil(this.dropContainer.dropBg.height));
    rt.draw(this.dropContainer, 0,0);

    rt.snapshot(async (image) => {
      try {
        await ABCyaLib.PrintSave.instance.saveImage(image, { fileName: ABCyaPhaser.Localization.instance.getLocalizedString('file_name') });
      }
      catch {

      }

      rt.destroy();
      blocker.destroy();
    });
  }

  recordRotateAction (e) {
    const a = new ItemAction({ action: 'ROTATE', itemRef: e.itemRef, rotation: e.rotation });
    this.actions.push(a);
  }

  createDragItem (e) {
    if (this.draggingItem) { // Bail if there is one
      return;
    }
    this.dropContainer.deselectItem();
    const menuNum = e.menuNum + 1;
    const subMenuNum = e.subMenuNum + 1;

    let spriteName = ''; let isFull = false; let isAnimated = false;
    const atlas = this.textures.get(gameData.game === Games.alphabet && !(e.menuNum === 2 || e.menuNum === 3) ? ABCyaPhaser.Images.LOCALIZATION_ATLAS : ABCyaPhaser.Images.DEFAULT_ATLAS);

    let animationCheck = null;
    let animationName = null;
    if (gameData.settings.HAS_ANIMATED === true) {
      const amenucheck = gameData.settings.ANIMATED_MENUS.indexOf(menuNum);
      if (amenucheck >= 0) {
        animationName = `Eyes${subMenuNum}`;
        animationCheck = this.anims.exists(animationName);
      }
    }
    if (animationCheck) {
      isAnimated = true;
      spriteName = this.anims.get(animationName).frames[0].frame.name;
    }
    else {
      const spriteNameCheck = atlas.has(`Submenu_${menuNum}_${subMenuNum}_Full`);
      if (!spriteNameCheck) {
        spriteName = `Submenu_${menuNum}_${subMenuNum}`;
      }
      else {
        isFull = true;
        spriteName = `Submenu_${menuNum}_${subMenuNum}_Full`;
      }
    }

    // See if there's a full sized version
    this.draggingItem = new Piece(this, spriteName, isFull, isAnimated, animationName, atlas.key);
    this.draggingItem.menuIndex = menuNum;
    this.draggingItem.submenuIndex = subMenuNum;
    this.draggingItem.startDragging(this.input.activePointer.x, this.input.activePointer.y);
    this.add.existing(this.draggingItem);
    this.draggingItem.once(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.releaseDragItem, this);

    this.dragOffsetX = 0;
    this.dragOffsetY = 0;

    this.tempAction = new ItemAction({ action: 'ADD', itemRef: this.draggingItem });
    this.isDragging = true;
    if (gameData.settings.GAME_NAME === 'Fridge Magnets') {
      this.cheapFridgePlay(this.draggingItem.menuIndex, this.draggingItem.submenuIndex);
    }
  }

  changeBaseItem (itemIndex) {
    const baseItemSpritename = `Base_${itemIndex + 1}`;
    this.dropContainer.changeBaseItem(baseItemSpritename);
  }

  handleMenuChange (e) {
    if (gameData.settings.FLIP_MENUS) {
      if (gameData.settings.FLIP_MENUS[e.menuIndex] === false) {
        this.isFlipable = false;
      }
      else {
        this.isFlipable = true;
      }
    }
  }

  layoutRatio () {
    this.menu.y = (this.scale.height - this.menu.subMenu.myRect.height) / 2;

    this.dropContainer.x = gameData.settings.DROP_BG_LOCS.nx;
    this.dropContainer.y = gameData.settings.DROP_BG_LOCS.ny;
    if (gameData.settings.GAME_NAME === 'Fridge Magnets') {
      this.buttonContainer.x = this.scale.width - this.buttonContainer.getBounds().height - 2;
      this.buttonContainer.y = this.scale.height / 2 + this.buttonContainer.getBounds().width / 2;
      this.buttonContainer.angle = 270;
    }
    else {
      this.buttonContainer.angle = 0;
      this.buttonContainer.x = this.dropContainer.x + (this.dropContainer.dropBg.width / 2) - this.buttonContainer.getBounds().width / 2;
      this.buttonContainer.y = this.scale.height - this.buttonContainer.getBounds().height;
    }
  }

  update(time, delta) {
    if (this.isDragging) {

      if (this.input.activePointer.x < 25 || this.input.activePointer.x > 1000 || this.input.activePointer.y < 25 || this.input.activePointer.y > 740) {
        // the mouse is being dragged out of the window, cancel the drag
        this.releaseDragItem();
      }
      else {
        this.draggingItem.x = this.input.activePointer.x + this.dragOffsetX;
        this.draggingItem.y = this.input.activePointer.y + this.dragOffsetY;
        const point = this.dropContainer.getLocalPoint(this.draggingItem.x, this.draggingItem.y);

        // Check for collision to show item is within drop range
        const alpha = this.textures.getPixelAlpha(point.x, point.y, this.dropContainer.dropBg.texture.key, this.dropContainer.dropBg.frame.name);

        if (alpha && alpha > 1) {
          this.draggingItem.showFullSize();
        }
        else {
          this.draggingItem.showPreviewSize();
        }

        if (this.isFlipable) {
          // Check for direction
          if (this.input.activePointer.x > this.lastDragX + 10) {
            this.draggingItem.turnRight();
          }
          else if (this.input.activePointer.x < this.lastDragX - 10) {
            this.draggingItem.turnLeft();
          }
          this.lastDragX = this.input.activePointer.x;
        }
      }
    }

    this.dropContainer.tick(time, delta);

    const count = this.animatedItems.length;
    for (let i = 0; i < count; i++) {
      this.animatedItems[i].tick(time);
    }
  }
}

function getLetterFromIndex(value) {
  if (ABCyaLib.Environment.instance.locale === ABCyaLib.Environment.LOCALES.esUS) {
    return 'aábcdeéfghiíjklmnñoópqrstuúüvwxyz'.split('')[value];
  }
  return 'abcdefghijklmnopqrstuvwxyz'.split('')[value];
}